// @ts-nocheck
import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';

class ShopifyAPI {
  /**
   * @param {string} handle - The product handle.
   * @returns {Promise<Object|null>} The product data or null if not found.
   */
  static async getProductByHandle(handle) {
    try {
      const res = await fetch(window.Shopify.routes.root + `products/${handle}.js`);
      if (!res.ok) {
        throw new Error(`Product not found: ${handle}`);
      }
      const productData = await res.json();
      return productData;
    } catch (error) {
      return null;
    }
  }

  /**
   * @param {string|null|undefined} url - The product URL.
   * @returns {string|null} The product handle or null.
   */
  static extractHandleFromUrl(url) {
    if (!url) return null;
    const match = url.match(/\/products\/([^\/\?]+)/);
    return match ? match[1] : null;
  }

  /**
   * @param {Object} product - The product object.
   * @param {string|number} variantId - The variant ID.
   * @returns {Object|null} The variant or null.
   */
  static getVariantById(product, variantId) {
    if (!product || !product.variants) return null;
    return product.variants.find((variant) => variant.id == variantId);
  }

  /**
   * @param {Object} swymItem - The Swym item object.
   * @returns {string|null} The product handle or null.
   */
  static getProductHandle(swymItem) {
    if (swymItem?.du) {
      const handle = this.extractHandleFromUrl(swymItem.du);
      if (handle) return handle;
    }
    return null;
  }
}

/**
 * @typedef {object} WishlistComponentRefs
 * @property {HTMLElement} wishlistContainer - The container for wishlist items.
 * @property {HTMLElement} countContainer - The container for wishlist count.
 * @property {HTMLElement} countContent - The count content element.
 * @property {HTMLElement} count - The count element.
 * @property {HTMLElement} buttonContainer - The button container.
 * @property {HTMLElement} buttonsContainer - The buttons container.
 * @property {HTMLButtonElement} shareButton - The share button.
 * @property {HTMLButtonElement} deleteAllButton - The delete all button.
 *
 * @extends {Component<WishlistComponentRefs>}
 */
export class WishlistComponent extends Component {
  requiredRefs = ['wishlistContainer', 'countContainer', 'count'];

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number | undefined} */
  #notificationTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();

    if (!window.SwymCallbacks) {
      window.SwymCallbacks = [];
    }

    window.SwymCallbacks.push((swat) => {
      this.#onSwymLoad(swat);
    });

    if (typeof window._swat !== 'undefined') {
      this.#onSwymLoad(window._swat);
    }

    this.#initializeButtons();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    if (this.#notificationTimeout) {
      clearTimeout(this.#notificationTimeout);
    }
  }

  #initializeButtons() {
    const { shareButton, deleteAllButton } = this.refs;

    if (shareButton) {
      shareButton.addEventListener('click', this.#handleShareButtonClick.bind(this));
    }

    if (deleteAllButton) {
      deleteAllButton.addEventListener('click', this.#handleDeleteAllClick.bind(this));
    }
  }

  /**
   * @param {Object} swat - The Swym service object.
   */
  #onSwymLoad(swat) {
    this.loadWishlist();
  }

  loadWishlist() {
    if (typeof window._swat === 'undefined') {
      return;
    }

    window._swat.fetchLists({
      callbackFn: async (userLists) => {
        if (!userLists || userLists.length === 0) {
          this.#renderEmptyWishlist();
          return;
        }

        const defaultList = userLists[0];
        if (defaultList && defaultList.lid) {
          const wishlistItems = defaultList.listcontents || [];

          if (wishlistItems.length === 0) {
            this.#renderEmptyWishlist();
          } else {
            await this.#renderWishlistItems(wishlistItems);
            this.#updateWishlistCount(wishlistItems.length);
            this.#hideButtonsSkeleton();
          }
        } else {
          this.#renderEmptyWishlist();
        }
      },
      errorFn: () => {
        this.#renderEmptyWishlist();
      },
    });
  }

  #renderEmptyWishlist() {
    const { wishlistContainer } = this.refs;
    if (wishlistContainer) {
      wishlistContainer.innerHTML = '';
    }
    this.#updateWishlistCount(0);
    this.#hideButtonsSkeleton();
    this.#hideSkeletonCards();
    this.#hideButtonContainer();
  }

  /**
   * @param {Array} listContents - The list of wishlist items.
   */
  async #renderWishlistItems(listContents) {
    try {
      const { wishlistContainer } = this.refs;
      if (!wishlistContainer) return;

      const productsWithData = await Promise.all(
        listContents.map(async (item) => {
          const handle = ShopifyAPI.getProductHandle(item);
          if (!handle) return null;
          const productData = await ShopifyAPI.getProductByHandle(handle);
          if (productData) {
            productData.selected_variant = item.epi;
            productData.wishlist_id = item.lid;
            productData.swym_epi = item.epi;
            productData.swym_empi = item.empi;
            productData.swym_du = item.du;
            productData.swym_lid = item.lid;
            return productData;
          }
          return null;
        })
      );

      const validProducts = productsWithData.filter((item) => item !== null);

      const productPromises = validProducts.map(async (item) => {
        try {
          if (!item.handle || !item.selected_variant) return null;
          const response = await fetch(
            window.Shopify.routes.root + `products/${item.handle}?variant=${item.selected_variant}&section_id=wishlist-cart`
          );
          let html = await response.text();

          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = html;
          const productCard = tempDiv.querySelector('product-card');
          if (productCard) {
            productCard.setAttribute('data-swym-epi', item.swym_epi);
            productCard.setAttribute('data-swym-empi', item.swym_empi);
            productCard.setAttribute('data-swym-du', item.swym_du);
            productCard.setAttribute('data-swym-lid', item.swym_lid);
          }

          return tempDiv.innerHTML;
        } catch (error) {
          return null;
        }
      });

      const productHtmls = await Promise.all(productPromises);
      const validHtmls = productHtmls.filter((html) => html !== null);

      if (wishlistContainer) {
        wishlistContainer.innerHTML = '';

        validHtmls.forEach((html) => {
          wishlistContainer.insertAdjacentHTML('beforeend', html);
        });

        this.#initializeDeleteButtons();
      }
    } catch (error) {
      const { wishlistContainer } = this.refs;
      if (wishlistContainer) {
        this.#hideSkeletonCards();
        wishlistContainer.innerHTML = '<div class="swym-error">Failed to load wishlist items</div>';
      }
    }
  }

  /**
   * @param {number} count - The count to display.
   */
  #updateWishlistCount(count) {
    const { countContainer, countContent, count: countElement } = this.refs;
    if (!countContainer || !countElement) return;

    countContainer.classList.remove('wishlist__count-container--skeleton');

    const skeleton = countContainer.querySelector('.wishlist__count-skeleton');
    if (skeleton) {
      skeleton.classList.add('hidden');
    }

    if (countContent) {
      countContent.classList.remove('hidden');
    }

    countContainer.classList.remove('hidden');
    countElement.textContent = String(count);
  }

  #hideButtonsSkeleton() {
    const { buttonContainer } = this.refs;
    if (!buttonContainer) return;

    const buttonsSkeleton = buttonContainer.querySelector('.wishlist__buttons-skeleton');
    if (buttonsSkeleton) {
      buttonsSkeleton.classList.add('hidden');
    }

    const buttonsContainer = buttonContainer.querySelector('.wishlist__buttons-container');
    if (buttonsContainer) {
      buttonsContainer.classList.remove('hidden');
    }
  }

  #hideSkeletonCards() {
    const { wishlistContainer } = this.refs;
    if (!wishlistContainer) return;

    const skeletonCards = wishlistContainer.querySelectorAll('.wishlist-skeleton__card');
    skeletonCards.forEach((card) => {
      card.classList.add('hidden');
    });
  }

  #hideButtonContainer() {
    const { buttonContainer } = this.refs;
    if (buttonContainer) {
      buttonContainer.classList.add('hidden');
    }
  }

  /**
   * @param {number} x - The X coordinate.
   * @param {number} y - The Y coordinate.
   */
  #showCopyNotification(x, y) {
    const existingNotification = document.querySelector('.wishlist__copy-notification');
    if (existingNotification) {
      existingNotification.remove();
    }

    const notification = document.createElement('div');
    notification.className = 'wishlist__copy-notification visually-hidden';
    notification.textContent = Theme?.translations?.wishlist_link_copied || 'Link copied to clipboard';

    notification.style.visibility = 'hidden';
    document.body.appendChild(notification);

    const notificationWidth = notification.offsetWidth;
    const notificationHeight = notification.offsetHeight;
    const offsetY = 20;

    let finalX = x;
    let finalY = y + offsetY;

    if (finalX + notificationWidth / 2 > window.innerWidth) {
      finalX = window.innerWidth - notificationWidth / 2 - 10;
    }
    if (finalX - notificationWidth / 2 < 0) {
      finalX = notificationWidth / 2 + 10;
    }
    if (finalY + notificationHeight > window.innerHeight) {
      finalY = y - notificationHeight - offsetY;
    }
    if (finalY < 0) {
      finalY = 10;
    }

    notification.style.left = finalX + 'px';
    notification.style.top = finalY + 'px';
    notification.style.transform = 'translateX(-50%)';
    notification.style.bottom = 'auto';
    notification.style.visibility = 'visible';

    requestAnimationFrame(() => {
      notification.classList.remove('visually-hidden');
    });

    if (this.#notificationTimeout) {
      clearTimeout(this.#notificationTimeout);
    }
    this.#notificationTimeout = setTimeout(() => {
      notification.classList.add('visually-hidden');
      setTimeout(() => {
        if (notification.parentNode) {
          notification.remove();
        }
      }, 300);
    }, 2000);
  }

  /**
   * @returns {boolean} True if customer is logged in, false otherwise.
   */
  #isCustomerLoggedIn() {
    const isLoggedIn = this.getAttribute('data-customer-logged-in');
    return isLoggedIn === 'true';
  }

  /**
   * @param {MouseEvent} event - The click event.
   */
  #handleShareButtonClick(event) {
    if (!this.#isCustomerLoggedIn()) {
      this.#showLoginRequiredModal();
      return;
    }

    if (typeof window._swat === 'undefined') {
      return;
    }

    const clickX = event ? event.clientX : window.innerWidth / 2;
    const clickY = event ? event.clientY : window.innerHeight / 2;

    window._swat.fetchLists({
      callbackFn: (userLists) => {
        if (!userLists || userLists.length === 0) {
          return;
        }

        const defaultList = userLists[0];
        if (!defaultList || !defaultList.lid) {
          return;
        }

        const lid = defaultList.lid;
        const onSuccess = () => {
          window._swat.generateSharedListURL(lid, (link) => {
            if (navigator.clipboard && window.isSecureContext) {
              navigator.clipboard.writeText(link).then(() => {
                this.#showCopyNotification(clickX, clickY);
              }).catch(() => {});
            } else {
              navigator.clipboard.writeText(link).then(() => {
                this.#showCopyNotification(clickX, clickY);
              }, () => {});
            }
          }, () => {});
        };

        window._swat.markListPublic(lid, onSuccess, () => {});
      },
      errorFn: () => {},
    });
  }

  /**
   * @param {string} type - The type of modal: 'delete' or 'login'.
   */
  #showConfirmModal(type) {
    const modal = document.getElementById('wishlist-confirm-modal');
    if (!modal) {
      return;
    }

    const messageElement = modal.querySelector('.wishlist-confirm-modal__message');
    const buttonsContainer = modal.querySelector('.wishlist-confirm-modal__buttons');
    if (!messageElement || !buttonsContainer) {
      return;
    }

    buttonsContainer.innerHTML = '';

    if (type === 'delete') {
      messageElement.textContent = Theme?.translations?.wishlist_delete_all_confirm || 
        'Are you sure you want to delete all items from your wishlist?';

      const confirmButton = document.createElement('button');
      confirmButton.className = 'button button-primary wishlist-confirm-modal__button wishlist-confirm-modal__button--yes';
      confirmButton.setAttribute('data-action', 'confirm');
      confirmButton.textContent = Theme?.translations?.wishlist_delete_confirm || 'Confirm';

      const cancelButton = document.createElement('button');
      cancelButton.className = 'button button-secondary wishlist-confirm-modal__button wishlist-confirm-modal__button--no';
      cancelButton.setAttribute('data-action', 'cancel');
      cancelButton.textContent = Theme?.translations?.wishlist_delete_cancel || 'Cancel';

      buttonsContainer.appendChild(confirmButton);
      buttonsContainer.appendChild(cancelButton);

      const handleButtonClick = (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const button = target.closest('[data-action]');
        if (!button) return;

        event.preventDefault();
        event.stopPropagation();

        const action = button.getAttribute('data-action');

        if (modal instanceof DialogComponent && modal.closeDialog) {
          modal.closeDialog();
        }

        if (action === 'confirm') {
          this.#deleteAllItems();
          this.#renderEmptyWishlist();
        }
      };

      const handleModalClose = () => {
        buttonsContainer.removeEventListener('click', handleButtonClick);
        modal.removeEventListener('dialog:close', handleModalClose);
      };

      buttonsContainer.addEventListener('click', handleButtonClick);
      modal.addEventListener('dialog:close', handleModalClose);

    } else if (type === 'login') {
      messageElement.textContent = Theme?.translations?.wishlist_share_requires_login || 
        'You cannot share your wishlist until you log in.';

      const loginUrl = this.getAttribute('data-login-url') || '/account/login';
      const loginLink = document.createElement('a');
      loginLink.href = loginUrl;
      loginLink.className = 'button button-primary wishlist-confirm-modal__button wishlist-confirm-modal__button--login';
      loginLink.setAttribute('data-action', 'login');
      loginLink.textContent = Theme?.translations?.actions_log_in || 'Log in';

      const cancelButton = document.createElement('button');
      cancelButton.className = 'button button-secondary wishlist-confirm-modal__button wishlist-confirm-modal__button--close';
      cancelButton.setAttribute('data-action', 'cancel');
      cancelButton.textContent = Theme?.translations?.wishlist_delete_cancel || 'Cancel';

      buttonsContainer.appendChild(loginLink);
      buttonsContainer.appendChild(cancelButton);

      const handleButtonClick = (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const button = target.closest('[data-action]');
        if (!button) return;

        const action = button.getAttribute('data-action');

        if (action === 'cancel') {
          event.preventDefault();
          event.stopPropagation();

          if (modal instanceof DialogComponent && modal.closeDialog) {
            modal.closeDialog();
          }
        }
      };

      const handleModalClose = () => {
        buttonsContainer.removeEventListener('click', handleButtonClick);
        modal.removeEventListener('dialog:close', handleModalClose);
      };

      buttonsContainer.addEventListener('click', handleButtonClick);
      modal.addEventListener('dialog:close', handleModalClose);
    }

    if (modal instanceof DialogComponent && modal.showDialog) {
      modal.showDialog();
    }
  }

  #showLoginRequiredModal() {
    this.#showConfirmModal('login');
  }

  #showDeleteConfirmModal() {
    this.#showConfirmModal('delete');
  }

  #deleteAllItems() {
    if (typeof window._swat === 'undefined') {
      return;
    }

    window._swat.fetchLists({
      callbackFn: (userLists) => {
        if (!userLists || userLists.length === 0) {
          this.#hideSkeletonCards();
          this.#hideButtonContainer();
          return;
        }

        const defaultList = userLists[0];
        if (!defaultList || !defaultList.lid) {
          return;
        }

        const lid = defaultList.lid;

        window._swat.deleteList(lid, () => {
          this.#updateWishlistCount(0);
          this.#renderWishlistItems([]);
        }, () => {});
      },
      errorFn: () => {},
    });
  }

  /**
   * @param {MouseEvent} event - The click event.
   */
  #handleDeleteAllClick(event) {
    event.preventDefault();
    this.#showDeleteConfirmModal();
  }

  /**
   * @param {Event} event - The click event.
   */
  #handleDeleteButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('.js-wishlist-delete-product');
    if (!button) return;

    const productCard = button.closest('product-card');
    if (!productCard) {
      return;
    }

    const epi = productCard.getAttribute('data-swym-epi');
    const empi = productCard.getAttribute('data-swym-empi');
    const du = productCard.getAttribute('data-swym-du');

    if (!epi || !empi || !du) {
      return;
    }

    if (typeof window._swat === 'undefined') {
      return;
    }

    window._swat.fetchLists({
      callbackFn: (userLists) => {
        if (!userLists || userLists.length === 0) {
          return;
        }

        const defaultList = userLists[0];
        if (!defaultList || !defaultList.lid) {
          return;
        }

        const lid = defaultList.lid;

        const product = {
          epi: epi,
          empi: empi,
          du: du,
        };

        window._swat.deleteFromList(lid, product, () => {
          this.loadWishlist();
        }, () => {});
      },
      errorFn: () => {},
    });
  }

  #initializeDeleteButtons() {
    const deleteButtons = this.refs.wishlistContainer?.querySelectorAll('.js-wishlist-delete-product');
    if (!deleteButtons) return;

    deleteButtons.forEach((button) => {
      const newButton = button.cloneNode(true);
      button.parentNode?.replaceChild(newButton, button);

      newButton.addEventListener('click', (e) => {
        this.#handleDeleteButtonClick(e);
      });
    });
  }
}

if (!customElements.get('wishlist-component')) {
  customElements.define('wishlist-component', WishlistComponent);
}
